import { cn } from '@/utils/cn'

export interface FilterChipProps {
  label: string
  count: number
  active: boolean
  onClick: () => void
}

/**
 * Pill toggle with a trailing count, used for status/type/pipeline filter bars.
 * Single source of truth — replaces the previously duplicated StatusFilterChip,
 * PipelineFilterChip, and TypeFilterChip.
 */
export function FilterChip({ label, count, active, onClick }: FilterChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors md:py-1',
        active
          ? 'border-primary bg-primary/10 text-foreground'
          : 'border-border text-muted-foreground hover:text-foreground hover:border-primary/40'
      )}
    >
      <span>{label}</span>
      <span
        className={cn(
          'inline-flex items-center justify-center rounded-full px-1.5 text-3xs tabular-nums',
          active ? 'bg-primary/20 text-foreground' : 'bg-muted text-muted-foreground'
        )}
      >
        {count}
      </span>
    </button>
  )
}

export interface FilterChipOption<T extends string> {
  value: T
  label: string
  count: number
}

export interface FilterChipGroupProps<T extends string> {
  /** The group's name for assistive technology. */
  legend: string
  /** The chip that clears the choice, with how many the whole group holds. */
  all: { label: string; count: number }
  options: readonly FilterChipOption<T>[]
  value: T | null
  onChange: (value: T | null) => void
}

/**
 * A single-choice filter bar: *All* first, then one chip per option; a second click on the chosen
 * chip goes back to all. Scrolls sideways on a phone instead of wrapping.
 */
export function FilterChipGroup<T extends string>({
  legend,
  all,
  options,
  value,
  onChange,
}: FilterChipGroupProps<T>) {
  return (
    <fieldset className="scrollbar-none flex flex-wrap items-center gap-2 max-md:-mx-4 max-md:flex-nowrap max-md:overflow-x-auto max-md:px-4 max-md:[&>*]:shrink-0">
      <legend className="sr-only">{legend}</legend>
      <FilterChip
        label={all.label}
        count={all.count}
        active={value === null}
        onClick={() => onChange(null)}
      />
      {options.map(option => (
        <FilterChip
          key={option.value}
          label={option.label}
          count={option.count}
          active={value === option.value}
          onClick={() => onChange(value === option.value ? null : option.value)}
        />
      ))}
    </fieldset>
  )
}
